import {
    FC,
    HTMLAttributes,
    RefObject,
    useEffect,
    useLayoutEffect,
    useRef,
} from 'react';
import { useMobileOrientation, isMobile, isAndroid } from 'react-device-detect';
import styled, { css, keyframes, useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Message } from '../Message';
import { CHAT_HEADER_HEIGHT_REM as CHAT_HEADER_HEIGHT_REM_MOBILE } from '../ChatHeader/mobile';
import { CHAT_HEADER_HEIGHT_REM as CHAT_HEADER_HEIGHT_REM_DESKTOP } from '../ChatHeader/desktop';
import { FOOTER_HEIGHT_REM } from '../Footer';
import { Messages } from '../../stores/chat';

const PADDING_TOP = isMobile
    ? CHAT_HEADER_HEIGHT_REM_MOBILE
    : CHAT_HEADER_HEIGHT_REM_DESKTOP;

type ChatMainProps = HTMLAttributes<HTMLDivElement> & {
    size?: number;
    disabled: boolean;
    messages: Messages[];
    users: string[];
    currentUser: string;
    pathname: string;
    endMessageRef: RefObject<HTMLDivElement>;
    messageContainerRef: RefObject<HTMLDivElement>;
    handleClick: (alias: string) => void;
};

type OrientationProps = {
    isPortrait: boolean;
    pathname?: string;
    size?: number;
};

const rem = (n: number) => `${n}rem`;

const getFooterRem = (pathname?: string) => FOOTER_HEIGHT_REM + 0.375;

const mobileStyles = css<OrientationProps>`
    position: fixed;
    bottom: auto;
    width: 46.875rem;
    max-width: 100%;

    ${({ pathname }) => {
        const footerRem = getFooterRem(pathname);
        const footerRemOnOpenKeyboard = rem(FOOTER_HEIGHT_REM + 0.375);
        const vhVarOnKeyboardOpen = isAndroid ? '--vh-actual' : '--vh-frozen';

        return css`
            /* Use viewport offset to keep in frame when keyboard opens */
            top: calc(var(--viewport-offset-top, 0px) + 17rem);

            /* Use frozen viewport height to maintain chat area height when keyboard opens */
            height: calc(
                var(--vh-frozen, 100vh) - var(--viewport-offset-top, 0px) -
                    16.5rem - ${rem(footerRem)}
            );

            padding: ${rem(PADDING_TOP + 1.875)} 0.375rem ${rem(footerRem)};

            html[data-keyboard-open='true'] & {
                top: calc(
                    var(--viewport-offset-top, 0px) +
                        ${rem(CHAT_HEADER_HEIGHT_REM_MOBILE)}
                );

                height: calc(
                    var(${vhVarOnKeyboardOpen}, 100vh) -
                        var(--viewport-offset-top, 0px) -
                        ${rem(CHAT_HEADER_HEIGHT_REM_MOBILE)} -
                        ${footerRemOnOpenKeyboard}
                        ${pathname === '/side' ? '+ 1rem' : ''}
                );

                padding: 3rem 0.375rem ${footerRemOnOpenKeyboard};
            }
        `;
    }}
`;

const desktopStyles = css<OrientationProps>`
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;

    top: ${({ size = 0 }) => rem(size)};
    padding: ${rem(PADDING_TOP + 0.5)} 0.375rem
        ${rem(FOOTER_HEIGHT_REM + 0.375)};
`;

const fontStyleCommon = css`
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    letter-spacing: -0.03rem;

    font-size: ${isMobile ? '1.875rem' : '1.25rem'};
`;

const ChatMainContainer = styled.div<{
    isPortrait: boolean;
    pathname?: string;
    size?: number;
}>`
    z-index: 10;
    border-radius: 1rem;
    background-color: ${({ theme }) => theme.chatMain.background};

    ${isMobile ? mobileStyles : desktopStyles}
`;

const ChatMainMask = styled(ChatMainContainer)`
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background-color: #000;
    opacity: 0.5;
`;

const ChatMainScrollContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: start;
    row-gap: 0.5rem;
    height: 100%;
    /* Only vertical scroll should be enabled for chat content */
    overflow-y: auto;
    overflow-x: hidden;

    padding: ${isMobile ? '0 1.75rem' : '0 1rem'};

    ${({ theme: { chatMain } }) => `
        background-color: ${chatMain.background};
    

        &::-webkit-scrollbar-track {
            border-radius: 0.75rem;
            background-color: ${chatMain.scroll.track};
            border-left: 0.438rem solid ${chatMain.border};
            border-right: 0.438rem solid ${chatMain.border};
        }

        &::-webkit-scrollbar {
            width: 1.625rem;
            background-color: transparent;
        }

        &::-webkit-scrollbar-thumb {
            border-radius: 0.75rem;
            background-color: ${chatMain.scroll.thumb};
        }
    `};
`;

const WelcomeContainer = styled.div`
    font-family: Roboto-Medium;
    font-weight: 500;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.4;
    letter-spacing: normal;

    font-size: ${isMobile ? '1.875rem' : '1.25rem'};
    margin-bottom: ${isMobile ? '2.188rem' : '1rem'};
    color: ${({ theme }) => theme.chatMain.color.welcome};
`;

const markDelete = keyframes`
    from { background-color: transparent; }
    to { background-color: rgba(220, 53, 69, 0.25); }
`;

const NameMessageContainer = styled.div<{ pendingDelete?: boolean }>`
    display: flex;
    align-items: start;
    border-radius: 0.25rem;
    ${({ pendingDelete }) =>
        pendingDelete &&
        css`
            animation: ${markDelete} 750ms ease forwards;
        `}
`;

const ChatName = styled.div<{ isChatHost?: boolean }>`
    ${fontStyleCommon};

    font-family: Roboto-Bold;
    margin-right: 0.375rem;
    cursor: pointer;
    color: ${({ isChatHost, theme }) =>
        isChatHost
            ? theme.chatMain.color.chathost.name
            : theme.chatMain.color.normal.name};
`;

const ChatMessage = styled.div<{ type: keyof typeof MESSAGE_TYPE }>`
    ${fontStyleCommon};

    font-family: Roboto;
    word-break: break-word;
    color: ${({ type, theme }) => theme.chatMain.color[type].message};
`;

const EndMessage = styled.div`
    margin-top: -5rem;
`;

const MESSAGE_TYPE = {
    chathost: 'chathost',
    normal: 'normal',
    system: 'system',
};

export const ChatMain: FC<ChatMainProps> = ({
    size,
    disabled,
    messages,
    users,
    currentUser,
    pathname,
    endMessageRef,
    messageContainerRef,
    handleClick,
    ...props
}) => {
    const { t } = useTranslation();
    const ref = useRef<Messages | null>(null);
    const isAtBottomRef = useRef<boolean>(true);
    const theme = useTheme();
    const orientation = useMobileOrientation();
    const { customEmoji } = theme.images;

    // Track whether user is at (or near) the bottom to decide auto-scroll behavior
    useEffect(() => {
        const container = messageContainerRef.current;
        if (!container) return;

        const threshold = 20; // px distance from bottom considered "at bottom"
        const onScroll = () => {
            const distanceFromBottom =
                container.scrollHeight -
                (container.scrollTop + container.clientHeight);
            isAtBottomRef.current = distanceFromBottom <= threshold;
        };

        // Initialize state based on current position
        onScroll();
        container.addEventListener('scroll', onScroll, { passive: true });
        return () => container.removeEventListener('scroll', onScroll);
    }, [messageContainerRef]);

    // On new messages, scroll if user is at bottom, or if current user sent the message.
    useLayoutEffect(() => {
        const last = messages[messages.length - 1];

        if (ref.current !== last) {
            ref.current = last;
            const container = messageContainerRef.current;
            if (container && last) {
                const sentByCurrentUser = last.alias === currentUser;
                if (sentByCurrentUser || isAtBottomRef.current) {
                    container.scrollTo({
                        top: container.scrollHeight,
                        behavior: 'auto',
                    });
                }
            }
        }
    }, [messages, currentUser, messageContainerRef]);

    // Scroll to bottom when keyboard opens
    useEffect(() => {
        const messageContainer = messageContainerRef.current;
        if (!messageContainer) return;

        const observer = new MutationObserver(() => {
            const isKeyboardOpen =
                document.documentElement.getAttribute('data-keyboard-open') ===
                'true';
            if (isKeyboardOpen) {
                // Use setTimeout to ensure layout has updated after keyboard opens
                setTimeout(() => {
                    messageContainer.scrollTo({
                        top: messageContainer.scrollHeight,
                        behavior: 'auto',
                    });
                }, 100);
            }
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-keyboard-open'],
        });

        return () => observer.disconnect();
    }, [messageContainerRef]);

    const getMessageKey = (message: Messages, index: number) => {
        if (message.messageId !== undefined) {
            return `message-${message.messageId}`;
        }

        return [
            message.isWelcomeMsg ? 'welcome' : 'chat',
            message.userId || message.alias || 'system',
            message.message,
            index,
        ].join('-');
    };

    return (
        <ChatMainContainer
            {...props}
            isPortrait={orientation.isPortrait}
            size={size}
            pathname={pathname}
        >
            <ChatMainScrollContainer ref={messageContainerRef}>
                {messages.map((message, index) => {
                    const messageKey = getMessageKey(message, index);

                    if (message.isWelcomeMsg) {
                        return (
                            <WelcomeContainer key={messageKey}>
                                {message.message}
                            </WelcomeContainer>
                        );
                    }

                    const alias = message.alias;

                    let type: keyof typeof MESSAGE_TYPE = 'normal';

                    if (message.ch) {
                        type = 'chathost';
                    }

                    if (message.bannedWord || message.isSystemMsg) {
                        type = 'system';
                    }

                    return (
                        <NameMessageContainer
                            key={messageKey}
                            pendingDelete={message.pendingDelete}
                        >
                            <ChatName
                                isChatHost={message.ch}
                                onClick={
                                    alias && alias !== currentUser
                                        ? () => handleClick(alias)
                                        : undefined
                                }
                            >
                                {alias ? (
                                    <>
                                        {message.ch ? `#${alias}#` : alias}
                                        {message.pm
                                            ? ` (${t('chatModal.private')})`
                                            : ''}
                                        {':'}
                                    </>
                                ) : (
                                    ''
                                )}
                            </ChatName>
                            <ChatMessage type={type}>
                                <Message
                                    usersMap={users}
                                    text={message.message}
                                    imageMap={customEmoji}
                                    isChatHost={message.ch}
                                />
                            </ChatMessage>
                        </NameMessageContainer>
                    );
                })}
                <EndMessage ref={endMessageRef} />
            </ChatMainScrollContainer>
            {disabled && <ChatMainMask isPortrait={orientation.isPortrait} />}
        </ChatMainContainer>
    );
};
